import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCelebration } from '@/context/CelebrationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Check, Gift, Heart, Mail, MessageSquare, Phone } from 'lucide-react';
import { BRAND_IMAGES } from '@/constants/brandImages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { celebrate, grandCelebrate } = useCelebration();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notificationPreference, setNotificationPreference] = useState('email');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Gift membership state
  const [giftCode, setGiftCode] = useState(null);
  const [giftData, setGiftData] = useState(null);

  // Check for gift code on mount
  useEffect(() => {
    const code = searchParams.get('gift') || sessionStorage.getItem('gift_code');
    if (code) {
      setGiftCode(code);
      // Pre-fill from sessionStorage if available
      const storedEmail = sessionStorage.getItem('gift_email');
      const storedName = sessionStorage.getItem('gift_name');
      if (storedEmail) setEmail(storedEmail);
      if (storedName) setFullName(storedName);
      
      // Fetch gift data
      fetchGiftData(code);
    }
  }, [searchParams]);

  const fetchGiftData = async (code) => {
    try {
      const response = await axios.get(`${API_URL}/api/gift/validate/${code}`);
      setGiftData(response.data);
      if (response.data.recipient_email) setEmail(response.data.recipient_email);
      if (response.data.recipient_name) setFullName(response.data.recipient_name);
    } catch (error) {
      console.error('Gift validation error:', error);
      // Gift might be invalid, but don't block registration
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validate phone number if SMS is selected
    if ((notificationPreference === 'sms' || notificationPreference === 'both') && !phoneNumber) {
      toast.error('Phone number is required for SMS notifications');
      return;
    }
    
    setLoading(true);
    
    try {
      // Register the user with notification preferences
      const user = await register(email, password, fullName, phoneNumber || null, notificationPreference);
      
      // If there's a gift code, accept it after registration
      if (giftCode) {
        try {
          await axios.post(`${API_URL}/api/gift/accept/${giftCode}`);
          grandCelebrate(`Welcome home, ${fullName.split(' ')[0]}! Your gift has been activated! 💝`);
          
          // Clear gift data from sessionStorage
          sessionStorage.removeItem('gift_code');
          sessionStorage.removeItem('gift_email');
          sessionStorage.removeItem('gift_name');
        } catch (giftError) {
          console.error('Gift acceptance error:', giftError);
          celebrate('confetti', 'Welcome home, Sis! Your sanctuary awaits.');
        }
      } else {
        celebrate('confetti', 'Welcome home, Sis! Your sanctuary awaits.');
      }
      
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'A sacred sisterhood that sees you',
    'Soul-centered coaching and guidance',
    'Safe spaces to speak what\'s sacred',
    'Restoration library for your wholeness',
    'Permission to rest and become'
  ];

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Left Side - Branding with Image */}
      <div className="hidden lg:flex flex-1 hero-gradient items-center justify-center p-12 relative overflow-hidden">
        {/* Background Image Overlay */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src={BRAND_IMAGES.wellness.joyful} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-lg text-white relative z-10">
          <h1 className="font-heading text-5xl font-bold mb-6">
            You Deserve a Space Where You Don&apos;t Have to Prove Anything
          </h1>
          <p className="text-xl text-white/80 mb-8">
            Step into a sisterhood of brilliant Black women who see you, support you, and celebrate your becoming.
          </p>
          
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-white/90">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-forest hover:text-terracotta mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          {/* Gift Banner */}
          {giftData && (
            <div className="mb-6 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-200">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="h-5 w-5 text-pink-600" />
                <Badge className="bg-pink-100 text-pink-700">
                  <Heart className="h-3 w-3 mr-1" /> Gifted Membership
                </Badge>
              </div>
              <p className="text-sm text-gray-700">
                <strong className="text-pink-700">{giftData.gifter_name}</strong> has gifted you a free month!
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Complete registration to activate your gift.
              </p>
            </div>
          )}

          <Card className="card-base border-none shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-3xl text-forest">
                {giftData ? 'Accept Your Gift' : 'Create Your Sanctuary'}
              </CardTitle>
              <CardDescription>
                {giftData ? 'Set up your account to activate your free month' : 'Take the first step into sisterhood'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    className="input-base"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-base"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="input-base pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-forest"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>

                {/* Notification Preferences */}
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium">How would you like to receive notifications?</Label>
                  <RadioGroup 
                    value={notificationPreference} 
                    onValueChange={setNotificationPreference}
                    className="grid grid-cols-3 gap-2"
                    data-testid="notification-preference-group"
                  >
                    <div>
                      <RadioGroupItem value="email" id="email-only" className="peer sr-only" />
                      <Label
                        htmlFor="email-only"
                        className="flex flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-3 hover:bg-sand/30 peer-data-[state=checked]:border-terracotta peer-data-[state=checked]:bg-terracotta/10 cursor-pointer transition-all"
                      >
                        <Mail className="h-5 w-5 mb-1 text-forest" />
                        <span className="text-xs font-medium">Email</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="sms" id="sms-only" className="peer sr-only" />
                      <Label
                        htmlFor="sms-only"
                        className="flex flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-3 hover:bg-sand/30 peer-data-[state=checked]:border-terracotta peer-data-[state=checked]:bg-terracotta/10 cursor-pointer transition-all"
                      >
                        <MessageSquare className="h-5 w-5 mb-1 text-forest" />
                        <span className="text-xs font-medium">Text</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="both" id="both" className="peer sr-only" />
                      <Label
                        htmlFor="both"
                        className="flex flex-col items-center justify-center rounded-lg border-2 border-gray-200 p-3 hover:bg-sand/30 peer-data-[state=checked]:border-terracotta peer-data-[state=checked]:bg-terracotta/10 cursor-pointer transition-all"
                      >
                        <div className="flex gap-1 mb-1">
                          <Mail className="h-4 w-4 text-forest" />
                          <MessageSquare className="h-4 w-4 text-forest" />
                        </div>
                        <span className="text-xs font-medium">Both</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Phone Number - Show if SMS selected */}
                {(notificationPreference === 'sms' || notificationPreference === 'both') && (
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phoneNumber"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required={notificationPreference === 'sms' || notificationPreference === 'both'}
                      className="input-base"
                      data-testid="register-phone-input"
                    />
                    <p className="text-xs text-gray-500">We&apos;ll send you session reminders and important updates</p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full btn-primary h-12" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-forest font-medium hover:text-terracotta">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
